import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  Search,
  Award,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  User,
  ChevronDown,
  ChevronUp,
  Star,
  Shield,
  Eye
} from 'lucide-react';

const GET_SKILL_VERIFICATIONS = gql`
  query GetSkillVerifications($status: String, $limit: Int) {
    skillVerifications(status: $status, limit: $limit) {
      id
      userId
      userEmail
      userName
      skillId
      skillName
      skillCategory
      skillTier
      status
      tokenReward
      tokensAwarded
      submittedAt
      reviewedAt
      reviewNotes
      rejectionReason
      verificationData {
        experienceYears
        description
        documents {
          type
          url
          fileName
        }
        references {
          name
          email
          relationship
        }
      }
    }
  }
`;

const GET_SKILL_STATS = gql`
  query GetSkillStats {
    skillVerificationStats {
      pending
      underReview
      approved
      rejected
      total
      byCategory {
        category
        count
      }
    }
  }
`;

const APPROVE_SKILL = gql`
  mutation ApproveSkillVerification($id: ID!, $notes: String, $bonusTokens: Int) {
    approveSkillVerification(id: $id, notes: $notes, bonusTokens: $bonusTokens) {
      id
      status
      tokenReward
    }
  }
`;

const REJECT_SKILL = gql`
  mutation RejectSkillVerification($id: ID!, $reason: String!) {
    rejectSkillVerification(id: $id, reason: $reason) {
      id
      status
    }
  }
`;

const MARK_UNDER_REVIEW = gql`
  mutation MarkSkillUnderReview($id: ID!) {
    markSkillUnderReview(id: $id) {
      id
      status
    }
  }
`;

const AdminSkills = () => {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSkill, setExpandedSkill] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [approvalData, setApprovalData] = useState({ notes: '', bonusTokens: 0 });
  const [rejectReason, setRejectReason] = useState('');

  const { loading, error, data, refetch } = useQuery(GET_SKILL_VERIFICATIONS, {
    variables: {
      status: filterStatus !== 'ALL' ? filterStatus : undefined,
      limit: 100
    },
    pollInterval: 30000
  });

  const { data: statsData, refetch: refetchStats } = useQuery(GET_SKILL_STATS);

  const [approveSkill] = useMutation(APPROVE_SKILL, {
    onCompleted: () => {
      refetch();
      refetchStats();
      setShowApprovalModal(false);
      setSelectedVerification(null);
    }
  });

  const [rejectSkill] = useMutation(REJECT_SKILL, {
    onCompleted: () => {
      refetch();
      refetchStats();
      setShowRejectModal(false);
      setSelectedVerification(null);
    }
  });

  const [markUnderReview] = useMutation(MARK_UNDER_REVIEW, {
    onCompleted: () => {
      refetch();
      refetchStats();
    }
  });

  const verifications = data?.skillVerifications || [];
  const stats = statsData?.skillVerificationStats || {};

  const filteredVerifications = verifications.filter(v => {
    const matchesSearch = 
      v.skillName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.userName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'ALL' || v.skillCategory === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 1: return 'bg-gray-100 text-gray-700';
      case 2: return 'bg-blue-100 text-blue-700';
      case 3: return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = (verification) => {
    setSelectedVerification(verification);
    setApprovalData({ notes: '', bonusTokens: 0 });
    setShowApprovalModal(true);
  };

  const handleReject = (verification) => {
    setSelectedVerification(verification);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const submitApproval = () => {
    approveSkill({
      variables: {
        id: selectedVerification.id,
        notes: approvalData.notes,
        bonusTokens: approvalData.bonusTokens
      }
    });
  };

  const submitRejection = () => {
    rejectSkill({
      variables: {
        id: selectedVerification.id,
        reason: rejectReason
      }
    });
  };

  // Get unique categories
  const categories = [...new Set(verifications.map(v => v.skillCategory))];

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-red-600">Error loading skills: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Skills Verification</h1>
              <p className="text-sm text-gray-600">Review and approve user skill certifications</p>
            </div>
          </div>
          <button
            onClick={() => { refetch(); refetchStats(); }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white px-6 py-4 border-b">
        <div className="grid grid-cols-5 gap-4">
          <div 
            className="text-center cursor-pointer hover:bg-yellow-50 rounded p-2"
            onClick={() => setFilterStatus('pending')}
          >
            <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div 
            className="text-center cursor-pointer hover:bg-blue-50 rounded p-2"
            onClick={() => setFilterStatus('under_review')}
          >
            <div className="text-2xl font-bold text-blue-600">{stats.underReview || 0}</div>
            <div className="text-sm text-gray-600">Under Review</div>
          </div>
          <div 
            className="text-center cursor-pointer hover:bg-green-50 rounded p-2"
            onClick={() => setFilterStatus('approved')}
          >
            <div className="text-2xl font-bold text-green-600">{stats.approved || 0}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </div>
          <div 
            className="text-center cursor-pointer hover:bg-red-50 rounded p-2"
            onClick={() => setFilterStatus('rejected')}
          >
            <div className="text-2xl font-bold text-red-600">{stats.rejected || 0}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{stats.total || 0}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
        </div>
      </div>

      {/* Category Stats */}
      {stats.byCategory?.length > 0 && (
        <div className="bg-white px-6 py-3 border-b">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">Pending by category:</span>
            {stats.byCategory.map(cat => (
              <span 
                key={cat.category}
                className="px-2 py-1 bg-gray-100 rounded cursor-pointer hover:bg-gray-200"
                onClick={() => setFilterCategory(cat.category)}
              >
                {cat.category}: <strong>{cat.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white px-6 py-4 border-b">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by skill name or user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Verifications List */}
      <div className="p-6">
        {filteredVerifications.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <Award size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No skill verifications found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredVerifications.map((verification) => (
              <div key={verification.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSkill(expandedSkill === verification.id ? null : verification.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Award className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{verification.skillName}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTierColor(verification.skillTier)}`}>
                            Tier {verification.skillTier}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                          <span className="flex items-center gap-1">
                            <User size={14} />
                            {verification.userName || verification.userEmail}
                          </span>
                          <span>{verification.skillCategory}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-bold">{verification.tokenReward || 0}</div>
                        <div className="text-xs text-gray-500">tokens</div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(verification.status)}`}>
                        {verification.status.replace('_', ' ')}
                      </span>
                      {expandedSkill === verification.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedSkill === verification.id && (
                  <div className="border-t p-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column - User Info */}
                      <div>
                        <h4 className="font-medium mb-3">Applicant Details</h4>
                        <div className="space-y-2 text-sm">
                          <p><strong>Name:</strong> {verification.userName}</p>
                          <p><strong>Email:</strong> {verification.userEmail}</p>
                          <p><strong>Submitted:</strong> {formatDate(verification.submittedAt)}</p>
                          {verification.verificationData?.experienceYears && (
                            <p><strong>Experience:</strong> {verification.verificationData.experienceYears} years</p>
                          )}
                        </div>

                        {verification.verificationData?.description && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">Description</h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded border">
                              {verification.verificationData.description}
                            </p>
                          </div>
                        )}

                        {verification.verificationData?.references?.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">References</h4>
                            <div className="space-y-2">
                              {verification.verificationData.references.map((ref, i) => (
                                <div key={i} className="bg-white p-2 rounded border text-sm">
                                  <p><strong>{ref.name}</strong> ({ref.relationship})</p>
                                  <p className="text-gray-600">{ref.email}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right Column - Documents */}
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <FileText size={16} />
                          Documents ({verification.verificationData?.documents?.length || 0})
                        </h4>
                        {verification.verificationData?.documents?.length > 0 ? (
                          <div className="space-y-2">
                            {verification.verificationData.documents.map((doc, i) => (
                              <div key={i} className="bg-white p-3 rounded border">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-medium capitalize">{doc.type}</span>
                                    {doc.fileName && (
                                      <p className="text-sm text-gray-500">{doc.fileName}</p>
                                    )}
                                  </div>
                                  <a 
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                                  >
                                    <Eye size={16} />
                                    View
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No documents submitted</p>
                        )}

                        {/* Review Info */}
                        {verification.reviewedAt && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">Review</h4>
                            <div className={`p-3 rounded ${verification.status === 'approved' ? 'bg-green-50' : verification.status === 'rejected' ? 'bg-red-50' : 'bg-blue-50'}`}>
                              <p className="text-sm">
                                <strong>Reviewed:</strong> {formatDate(verification.reviewedAt)}
                              </p>
                              {verification.reviewNotes && (
                                <p className="text-sm mt-1">{verification.reviewNotes}</p>
                              )}
                              {verification.rejectionReason && (
                                <p className="text-sm mt-1 text-red-700">{verification.rejectionReason}</p>
                              )}
                              {verification.tokensAwarded && (
                                <p className="text-sm mt-1 text-green-700">
                                  ✓ {verification.tokenReward} tokens awarded
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {verification.status === 'pending' && (
                      <div className="mt-4 pt-4 border-t flex gap-3">
                        <button
                          onClick={() => markUnderReview({ variables: { id: verification.id } })}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Eye size={18} />
                          Mark Under Review
                        </button>
                        <button
                          onClick={() => handleApprove(verification)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <CheckCircle size={18} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(verification)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          <XCircle size={18} />
                          Reject
                        </button>
                      </div>
                    )}

                    {verification.status === 'under_review' && (
                      <div className="mt-4 pt-4 border-t flex gap-3">
                        <button
                          onClick={() => handleApprove(verification)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <CheckCircle size={18} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(verification)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          <XCircle size={18} />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Approve Skill Verification</h3>
            <p className="text-sm text-gray-600 mb-4">
              Approving <strong>{selectedVerification.skillName}</strong> for {selectedVerification.userName} will award <strong>{selectedVerification.tokenReward} tokens</strong>.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Bonus Tokens (optional)</label>
                <input
                  type="number"
                  value={approvalData.bonusTokens}
                  onChange={(e) => setApprovalData({...approvalData, bonusTokens: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total: {selectedVerification.tokenReward + approvalData.bonusTokens} tokens
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  value={approvalData.notes}
                  onChange={(e) => setApprovalData({...approvalData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Verification notes..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitApproval}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Reject Skill Verification</h3>
            <p className="text-sm text-gray-600 mb-4">
              Rejecting <strong>{selectedVerification.skillName}</strong> for {selectedVerification.userName}.
            </p>
            
            <div>
              <label className="block text-sm font-medium mb-1">Reason for Rejection</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="Please explain why this verification is being rejected..."
                required
              />
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitRejection}
                disabled={!rejectReason.trim()}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-300"
              >
                Reject
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSkills;